import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import CampaignStudioModal from "@/hq/campaigns/CampaignStudioModal";
import HQPitches from "@/hq/campaigns/HQPitches";
import HQActivityFeed from "@/hq/campaigns/HQActivityFeed";
import { asText } from "@/hq/asText";
import {
  Send, Users, CheckCircle2, Circle, Palette, Sparkles, ArrowRight,
} from "lucide-react";

const TABS = [
  { key: "overview",   label: "Overview" },
  { key: "annual",     label: "Annual Campaigns" },
  { key: "product",    label: "Product Campaigns" },
  { key: "intro",      label: "Intro Campaign" },
  { key: "pitches",    label: "Pitches" },
  { key: "activity",   label: "Activity" },
];

function KPI({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{label}</div>
      <div className="mt-2 font-black text-2xl">{value ?? "—"}</div>
    </Card>
  );
}

function CampaignCard({ tpl, onOpen, testIdPrefix }) {
  const isAnnual = tpl.audience_mode === "auto_all_eligible";
  return (
    <Card className="p-6 flex flex-col justify-between h-full cursor-pointer hover:border-[#FF6A00] transition-colors" onClick={() => onOpen(tpl)} data-testid={`${testIdPrefix}-${tpl.key}`}>
      <div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{tpl.eyebrow || tpl.label}</div>
            <h3 className="mt-1 font-black text-lg leading-tight">{tpl.label}</h3>
          </div>
          {tpl.enabled === false ? (
            <Circle className="text-[#8A8A8A] shrink-0" size={18}/>
          ) : (
            <CheckCircle2 className="text-[#0FA958] shrink-0" size={18}/>
          )}
        </div>
        <p className="mt-3 text-[13px] text-[#4A4A4A] line-clamp-3">{tpl.subject}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-[10px] tracking-[0.16em] uppercase font-bold">
          <span className="px-2 py-1 rounded-full bg-[#FFF7EE] text-[#8A4C00]">{isAnnual ? "Auto · all eligible" : "Manual audience"}</span>
          {tpl.last_sent_at && <span className="px-2 py-1 rounded-full bg-[#F6F5F2] text-[#4A4A4A]">Last sent {new Date(tpl.last_sent_at).toLocaleDateString()}</span>}
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-[#EAEAEA] flex items-center justify-between text-[11px] font-black tracking-[0.16em] uppercase text-[#FF6A00]">
        <span>Preview &amp; send</span>
        <ArrowRight size={14}/>
      </div>
    </Card>
  );
}

export default function HQCampaignsHub() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [studio, setStudio] = useState({ open: false, kind: null, key: null });
  const navigate = useNavigate();

  const loadTemplates = () => hqApi.get("/control/studio/templates").then((r) => setTemplates(r.data.items));
  useEffect(() => {
    hqApi.get("/sales/dashboard").then((r) => setOverview(r.data)).catch(() => setOverview({}));
    loadTemplates();
  }, []);

  const openStudio = (tpl) => setStudio({ open: true, kind: tpl.kind, key: tpl.key });
  const closeStudio = () => { setStudio({ open: false, kind: null, key: null }); loadTemplates(); };

  const annual = (templates || []).filter((t) => t.kind === "annual");
  const product = (templates || []).filter((t) => t.kind === "product");
  const intro = (templates || []).filter((t) => t.kind === "intro");

  return (
    <div className="space-y-5" data-testid="hq-campaigns-hub">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Campaigns</div>
          <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">Ready-made campaign studio</h1>
          <p className="mt-1 text-sm text-[#4A4A4A]">Every campaign below is a polished OrangeBySite email. Click any card to preview, edit and send.</p>
        </div>
        <button
          data-testid="hq-create-custom-campaign"
          onClick={() => navigate("/hq/sales/campaigns")}
          className="px-4 py-2.5 rounded-full bg-white border border-[#EAEAEA] hover:border-[#FF6A00] text-[11px] font-black tracking-[0.16em] uppercase"
        >
          <Palette size={13} className="inline mr-1"/> Create custom campaign
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto" data-testid="hq-campaigns-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`hq-campaigns-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase whitespace-nowrap ${
              tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] hover:bg-[#F6F5F2]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Ready-made templates" value={templates?.length ?? "—"}/>
            <KPI label="Total campaign sends" value={overview?.campaigns?.total_sent}/>
            <KPI label="Opens" value={overview?.campaigns?.total_opens}/>
            <KPI label="Clicks" value={overview?.campaigns?.total_clicks}/>
          </div>
          <Card className="p-6">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Featured</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {templates?.slice(0, 6).map((t) => <CampaignCard key={`${t.kind}-${t.key}`} tpl={t} onOpen={openStudio} testIdPrefix="hq-featured"/>)}
            </div>
          </Card>
        </div>
      )}

      {tab === "annual" && (
        <div>
          <Card className="p-4 mb-4">
            <p className="text-sm text-[#4A4A4A]"><Sparkles size={14} className="inline text-[#FF6A00] mr-1"/> Six seasonal ready-made campaigns. Annual campaigns automatically send to ALL ELIGIBLE clients — the audience picker is auto-managed.</p>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="hq-annual-grid">
            {!templates ? [0, 1, 2, 3, 4, 5].map((i) => <Card key={i} className="p-6"><Skeleton h={100}/></Card>) : annual.map((t) => <CampaignCard key={t.key} tpl={t} onOpen={openStudio} testIdPrefix="hq-annual"/>)}
          </div>
        </div>
      )}

      {tab === "product" && (
        <div>
          <Card className="p-4 mb-4">
            <p className="text-sm text-[#4A4A4A]"><Users size={14} className="inline text-[#FF6A00] mr-1"/> Product campaigns require manual audience selection. Search, filter and pick who receives each send.</p>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="hq-product-grid">
            {!templates ? [0, 1, 2, 3, 4, 5].map((i) => <Card key={i} className="p-6"><Skeleton h={100}/></Card>) : product.map((t) => <CampaignCard key={t.key} tpl={t} onOpen={openStudio} testIdPrefix="hq-product"/>)}
          </div>
        </div>
      )}

      {tab === "intro" && (
        <div>
          <Card className="p-4 mb-4">
            <p className="text-sm text-[#4A4A4A]"><Send size={14} className="inline text-[#FF6A00] mr-1"/> Manual audience. Also available from any Client Profile as “Send introduction”.</p>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hq-intro-grid">
            {!templates ? <Card className="p-6"><Skeleton h={100}/></Card> : intro.map((t) => <CampaignCard key={t.key} tpl={t} onOpen={openStudio} testIdPrefix="hq-intro"/>)}
          </div>
        </div>
      )}

      {tab === "pitches" && <HQPitches/>}

      {tab === "activity" && (
        <div data-testid="hq-camp-activity">
          <HQActivityFeed testIdPrefix="hq-camp-activity-feed"/>
        </div>
      )}

      <CampaignStudioModal
        open={studio.open}
        kind={studio.kind}
        tplKey={studio.key}
        onClose={closeStudio}
        onSent={closeStudio}
      />
    </div>
  );
}
